import { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import {
  useCommunityAdminReports,
  useResolveCommunityReport,
  useDismissCommunityReport,
} from '../../hooks/use-admin';
import { getDiscussionPath } from '../../utils/slug';
import {
  Flag,
  Search,
  Filter,
  MoreVertical,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  Calendar,
  User,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  MessageSquare,
  MessageCircle,
  Clock,
  Shield,
  AlertCircle,
  Eye,
  Ban,
  Trash2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';

const getReasonLabel = (reason) => {
  const labels = {
    spam: 'Spam',
    harassment: 'Harassment',
    hate_speech: 'Hate Speech',
    misinformation: 'Misinformation',
    inappropriate: 'Inappropriate',
    inappropriate_content: 'Inappropriate Content',
    impersonation: 'Impersonation',
    copyright: 'Copyright',
    copyright_violation: 'Copyright Violation',
    off_topic: 'Off Topic',
    pornographic: 'Pornographic',
    violent: 'Violent',
    fraudulent: 'Fraudulent',
    stolen: 'Stolen',
    stolen_content: 'Stolen Content',
    scam: 'Scam',
    illegal: 'Illegal',
    illegal_content: 'Illegal Content',
    self_harm: 'Self Harm',
    self_harm_promotion: 'Self Harm Promotion',
    terrorism: 'Terrorism',
    doxxing: 'Doxxing',
    privacy_violation: 'Privacy Violation',
    csam: 'CSAM',
    other: 'Other',
  };
  return labels[reason] || reason;
};

const getPriorityInfo = (priority) => {
  switch (priority) {
    case 'critical':
      return {
        color: 'text-red-400 bg-red-500/20 border-red-500/30',
        label: 'Critical',
      };
    case 'high':
      return {
        color: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
        label: 'High',
      };
    case 'medium':
      return {
        color: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
        label: 'Medium',
      };
    case 'low':
      return {
        color: 'text-zinc-400 bg-zinc-500/20 border-zinc-500/30',
        label: 'Low',
      };
    default:
      return {
        color: 'text-zinc-400 bg-zinc-500/20 border-zinc-500/30',
        label: 'Unknown',
      };
  }
};

const getStatusInfo = (status) => {
  switch (status) {
    case 'pending':
      return {
        color: 'text-yellow-400 bg-yellow-500/20',
        label: 'Pending',
        icon: Clock,
      };
    case 'reviewing':
      return {
        color: 'text-blue-400 bg-blue-500/20',
        label: 'Reviewing',
        icon: Eye,
      };
    case 'resolved':
      return {
        color: 'text-emerald-400 bg-emerald-500/20',
        label: 'Resolved',
        icon: CheckCircle,
      };
    case 'dismissed':
      return {
        color: 'text-zinc-400 bg-zinc-500/20',
        label: 'Dismissed',
        icon: XCircle,
      };
    default:
      return {
        color: 'text-zinc-400 bg-zinc-500/20',
        label: status,
        icon: AlertCircle,
      };
  }
};

const getEntityIcon = (entityType) => {
  switch (entityType) {
    case 'discussion':
      return MessageSquare;
    case 'comment':
      return MessageCircle;
    default:
      return AlertCircle;
  }
};

export default function CommunityAdminReports() {
  const { community } = useOutletContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: null,
    report: null,
  });
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolution, setResolution] = useState('no_action');

  // Hooks
  const { data: reportsData, isLoading } = useCommunityAdminReports(
    community?.oid,
    {
      status: statusFilter,
      search: searchQuery,
      page,
      limit: 20,
    }
  );
  const resolveMutation = useResolveCommunityReport(community?.oid);
  const dismissMutation = useDismissCommunityReport(community?.oid);

  const reports = reportsData?.reports || [];
  const pagination = reportsData?.pagination || {
    page: 1,
    totalPages: 1,
    total: 0,
  };

  const handleResolve = async () => {
    await resolveMutation.mutateAsync({
      reportId: actionDialog.report?._id || actionDialog.report?.id,
      resolution,
      resolutionNotes,
    });
    setActionDialog({ open: false, type: null, report: null });
    setResolutionNotes('');
    setResolution('no_action');
  };

  const handleDismiss = async () => {
    await dismissMutation.mutateAsync({
      reportId: actionDialog.report?._id || actionDialog.report?.id,
      reason: resolutionNotes,
    });
    setActionDialog({ open: false, type: null, report: null });
    setResolutionNotes('');
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10 text-red-400">
              <Flag className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-white">Community Reports</CardTitle>
              <CardDescription className="text-zinc-400">
                Review and resolve reports from community members
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700 text-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem
                  value="all"
                  className="text-white hover:bg-zinc-700"
                >
                  All Reports
                </SelectItem>
                <SelectItem
                  value="pending"
                  className="text-white hover:bg-zinc-700"
                >
                  Pending
                </SelectItem>
                <SelectItem
                  value="reviewing"
                  className="text-white hover:bg-zinc-700"
                >
                  Reviewing
                </SelectItem>
                <SelectItem
                  value="resolved"
                  className="text-white hover:bg-zinc-700"
                >
                  Resolved
                </SelectItem>
                <SelectItem
                  value="dismissed"
                  className="text-white hover:bg-zinc-700"
                >
                  Dismissed
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reports List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
              <Flag className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No reports found</p>
              <p className="text-sm">
                {statusFilter === 'pending'
                  ? 'No pending reports to review'
                  : 'No reports match your filters'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {reports.map((report) => {
                const EntityIcon = getEntityIcon(report.entityType);
                const priorityInfo = getPriorityInfo(report.priority);
                const statusInfo = getStatusInfo(report.status);
                const StatusIcon = statusInfo.icon;

                return (
                  <div
                    key={report._id || report.id}
                    className="p-4 hover:bg-zinc-800/30 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      {/* Entity Icon */}
                      <div
                        className={cn(
                          'p-2 rounded-lg',
                          report.entityType === 'discussion'
                            ? 'bg-blue-500/10 text-blue-400'
                            : 'bg-violet-500/10 text-violet-400'
                        )}
                      >
                        <EntityIcon className="w-5 h-5" />
                      </div>

                      {/* Report Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            {/* Report Reason & Priority */}
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={cn(
                                  'px-2 py-0.5 rounded text-xs font-medium',
                                  priorityInfo.color
                                )}
                              >
                                {priorityInfo.label}
                              </span>
                              <span className="text-white font-medium">
                                {getReasonLabel(report.reason)}
                              </span>
                              <span
                                className={cn(
                                  'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                                  statusInfo.color
                                )}
                              >
                                <StatusIcon className="w-3 h-3" />
                                {statusInfo.label}
                              </span>
                            </div>

                            {/* Reported Content */}
                            <div className="text-sm text-zinc-400 mb-2">
                              <span className="capitalize">
                                {report.entityType}
                              </span>
                              {report.entityDetails && (
                                <>
                                  {report.entityType === 'discussion' &&
                                    report.entityDetails.title && (
                                      <span>
                                        : "{report.entityDetails.title}"
                                      </span>
                                    )}
                                  {report.entityType === 'comment' &&
                                    report.entityDetails.discussionTitle && (
                                      <span>
                                        {' '}
                                        on "
                                        {report.entityDetails.discussionTitle}"
                                      </span>
                                    )}
                                </>
                              )}
                            </div>

                            {/* Description */}
                            {report.description && (
                              <p className="text-sm text-zinc-500 line-clamp-2 mb-2">
                                {report.description}
                              </p>
                            )}

                            {/* Snapshot Preview */}
                            {report.contentSnapshot?.text && (
                              <div className="bg-zinc-800/50 rounded p-2 mb-2 border-l-2 border-zinc-600">
                                <p className="text-xs text-zinc-400 italic line-clamp-2">
                                  "{report.contentSnapshot.text}"
                                </p>
                              </div>
                            )}

                            {/* Meta Info */}
                            <div className="flex items-center gap-4 text-xs text-zinc-500">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                Reported by{' '}
                                {report.reporterUsername || 'Anonymous'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(report.createdAt)}
                              </span>
                              {report.reportedUsername && (
                                <span className="flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  Against {report.reportedUsername}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {/* View Content Link */}
                            {report.entityDetails &&
                              report.entityType === 'discussion' && (
                                <Link
                                  to={getDiscussionPath(
                                    {
                                      title: report.entityDetails.title,
                                      slug: report.entityDetails.slug,
                                      oid: report.entityDetails.oid,
                                    },
                                    community.slug
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-zinc-400 hover:text-white"
                                    title="Open in new tab"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                </Link>
                              )}
                            {report.entityDetails &&
                              report.entityType === 'comment' &&
                              report.entityDetails.discussionOid && (
                                <Link
                                  to={getDiscussionPath(
                                    {
                                      title:
                                        report.entityDetails.discussionTitle,
                                      slug: report.entityDetails.discussionSlug,
                                      oid: report.entityDetails.discussionOid,
                                    },
                                    community.slug
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-zinc-400 hover:text-white"
                                    title="Open in new tab"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                </Link>
                              )}

                            {/* Actions Dropdown */}
                            {report.status === 'pending' ||
                            report.status === 'reviewing' ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-zinc-400 hover:text-white"
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="bg-zinc-800 border-zinc-700"
                                >
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setActionDialog({
                                        open: true,
                                        type: 'resolve',
                                        report,
                                      })
                                    }
                                    className="text-emerald-400 hover:text-emerald-300 hover:bg-zinc-700 cursor-pointer"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Resolve Report
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-zinc-700" />
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setActionDialog({
                                        open: true,
                                        type: 'dismiss',
                                        report,
                                      })
                                    }
                                    className="text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700 cursor-pointer"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Dismiss Report
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : null}
                          </div>
                        </div>

                        {/* Resolution Info (if resolved/dismissed) */}
                        {(report.status === 'resolved' ||
                          report.status === 'dismissed') && (
                          <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                            <div className="flex items-center gap-2 text-xs text-zinc-400 mb-1">
                              <Shield className="w-3 h-3" />
                              {report.status === 'resolved'
                                ? 'Resolved'
                                : 'Dismissed'}{' '}
                              by {report.resolvedByUsername || 'Admin'}
                              {report.resolvedAt && (
                                <span>• {formatDate(report.resolvedAt)}</span>
                              )}
                            </div>
                            {report.resolution &&
                              report.resolution !== 'no_action' && (
                                <p className="text-xs text-zinc-300">
                                  Action: {report.resolution.replace(/_/g, ' ')}
                                </p>
                              )}
                            {report.resolutionNotes && (
                              <p className="text-xs text-zinc-500 mt-1">
                                Notes: {report.resolutionNotes}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <p className="text-sm text-zinc-400">
                Showing page {pagination.page} of {pagination.totalPages} (
                {pagination.total} reports)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPage((p) => Math.min(pagination.totalPages, p + 1))
                  }
                  disabled={page === pagination.totalPages}
                  className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolve Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === 'resolve'}
        onOpenChange={(open) =>
          !open && setActionDialog({ open: false, type: null, report: null })
        }
      >
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              Resolve Report
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Take action on this report and mark it as resolved.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Report Summary */}
            {actionDialog.report && (
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-sm text-zinc-300">
                  <span className="font-medium">
                    {getReasonLabel(actionDialog.report.reason)}
                  </span>
                  {' - '}
                  <span className="capitalize">
                    {actionDialog.report.entityType}
                  </span>
                </p>
                {actionDialog.report.description && (
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                    {actionDialog.report.description}
                  </p>
                )}
              </div>
            )}

            {/* Resolution Action */}
            <div className="space-y-2">
              <label className="text-sm text-zinc-300">Action Taken</label>
              <Select value={resolution} onValueChange={setResolution}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem
                    value="no_action"
                    className="text-white hover:bg-zinc-700"
                  >
                    No Action Required
                  </SelectItem>
                  <SelectItem
                    value="warning_issued"
                    className="text-white hover:bg-zinc-700"
                  >
                    Warning Issued
                  </SelectItem>
                  <SelectItem
                    value="content_removed"
                    className="text-white hover:bg-zinc-700"
                  >
                    Content Removed
                  </SelectItem>
                  <SelectItem
                    value="user_suspended"
                    className="text-white hover:bg-zinc-700"
                  >
                    User Suspended
                  </SelectItem>
                  <SelectItem
                    value="user_banned"
                    className="text-white hover:bg-zinc-700"
                  >
                    User Banned
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Resolution Notes */}
            <div className="space-y-2">
              <label className="text-sm text-zinc-300">
                Resolution Notes (Optional)
              </label>
              <Textarea
                placeholder="Add notes about how this report was resolved..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setActionDialog({ open: false, type: null, report: null })
              }
              className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              disabled={resolveMutation.isLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {resolveMutation.isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resolving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Resolve Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dismiss Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === 'dismiss'}
        onOpenChange={(open) =>
          !open && setActionDialog({ open: false, type: null, report: null })
        }
      >
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <XCircle className="w-5 h-5 text-zinc-400" />
              Dismiss Report
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Dismiss this report without taking action.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Report Summary */}
            {actionDialog.report && (
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-sm text-zinc-300">
                  <span className="font-medium">
                    {getReasonLabel(actionDialog.report.reason)}
                  </span>
                  {' - '}
                  <span className="capitalize">
                    {actionDialog.report.entityType}
                  </span>
                </p>
                {actionDialog.report.description && (
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                    {actionDialog.report.description}
                  </p>
                )}
              </div>
            )}

            {/* Dismiss Reason */}
            <div className="space-y-2">
              <label className="text-sm text-zinc-300">
                Reason for Dismissing (Optional)
              </label>
              <Textarea
                placeholder="Why is this report being dismissed..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setActionDialog({ open: false, type: null, report: null })
              }
              className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDismiss}
              disabled={dismissMutation.isLoading}
              className="bg-zinc-700 hover:bg-zinc-600 text-white"
            >
              {dismissMutation.isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Dismissing...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Dismiss Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
